import React, { useEffect } from "react";
import {
  Construction,
  Clock,
  CalendarClock,
  AlertCircle,
  ChevronRight,
} from "lucide-react";
import Header from "../components/Header";
import { Helmet } from "react-helmet";
// AdSense Component
const AdSenseDisplay = ({ slot, format = "auto" }) => {
  useEffect(() => {
    try {
      // Initialize AdSense ads after component mounts
      document.title = "Work in Progress | Coming Soon";
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (error) {
      console.error("AdSense initialization error:", error);
    }
  }, []);

  return (
    <div
      className="adSenseContainer my-8 rounded-lg overflow-hidden shadow-md"
      style={{ width: "100%", minWidth: "300px" }}>
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client="ca-pub-2067522743161930" // Replace with your actual AdSense publisher ID
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"></ins>
    </div>
  );
};

const WorkInProgress = () => {
  useEffect(() => {
    // Set page title
    document.title = "Work in Progress | Coming Soon";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col">
      <Header />
      <Helmet>
        <title>Work in progress</title>
        <meta
          name="description"
          content="Work in progress page for future developments."
        />
      </Helmet>
      <main className="flex-grow container mx-auto px-4 py-12 max-w-5xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-blue-100 rounded-full mb-6">
            <Construction size={40} className="text-blue-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            We're Working on Something Amazing!
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our team is actively building this page to bring you valuable
            content and resources. Please check back soon to see what we're
            creating.
          </p>
        </div>

        {/* AdSense placement 1: After header section */}
        <AdSenseDisplay slot="1234567890" />

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-duration-300">
            <div className="p-3 bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <Clock className="text-blue-600" size={24} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              Estimated Completion
            </h2>
            <p className="text-gray-600">
              We expect this page to be ready within the next few weeks. Our
              team is working diligently to ensure we deliver high-quality
              content that meets your needs.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-duration-300">
            <div className="p-3 bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <CalendarClock className="text-blue-600" size={24} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              What to Expect
            </h2>
            <p className="text-gray-600">
              When completed, this page will feature comprehensive guides,
              resources, and tools to help you navigate this topic with
              confidence and clarity.
            </p>
          </div>
        </div>

        {/* AdSense placement 2: Between content sections */}
        <AdSenseDisplay slot="2345678901" />

        <div className="bg-blue-50 p-8 rounded-xl border border-blue-100 mb-16">
          <div className="flex items-start space-x-4">
            <div className="p-2 bg-yellow-100 rounded-full mt-1">
              <AlertCircle className="text-yellow-600" size={24} />
            </div>
            <div>
              {/* <h3 className="text-xl font-bold text-gray-800 mb-2">Stay Updated</h3>
              <p className="text-gray-600 mb-4">
                Want to know when this page goes live? Subscribe to our newsletter and you'll be among 
                the first to access our new content when it's ready.
              </p> */}

              {/* <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <input 
                  type="email" 
                  placeholder="Your email address" 
                  className="flex-grow px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center">
                  Notify Me <ChevronRight size={18} className="ml-1" />
                </button>
              </div> */}
            </div>
          </div>
        </div>

        {/* AdSense placement 3: Before footer */}
        <AdSenseDisplay slot="3456789012" />

        <div className="bg-white p-8 rounded-xl shadow-md">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            Explore Other Content
          </h3>
          <p className="text-gray-600 mb-6">
            While you wait, check out these other resources that might be
            helpful:
          </p>

          <div className="space-y-4">
            <a
              href="/"
              className="block p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-800">
                  Introduction to Our Services
                </span>
                <ChevronRight size={20} className="text-blue-600" />
              </div>
            </a>

            <a
              href="/faq"
              className="block p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-800">
                  Frequently Asked Questions
                </span>
                <ChevronRight size={20} className="text-blue-600" />
              </div>
            </a>

            <a
              href="/contact"
              className="block p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-800">
                  Contact Our Support Team
                </span>
                <ChevronRight size={20} className="text-blue-600" />
              </div>
            </a>
          </div>
        </div>
      </main>

      <footer className="bg-gray-50 border-t border-gray-200 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600 mb-4">
            © {new Date().getFullYear()} Relationship Wellness. All rights
            reserved.
          </p>
          <div className="flex justify-center space-x-6">
            <a href="/privacy" className="text-gray-500 hover:text-gray-700">
              Privacy Policy
            </a>
            <a href="/terms" className="text-gray-500 hover:text-gray-700">
              Terms of Service
            </a>
            <a href="/contact" className="text-gray-500 hover:text-gray-700">
              Contact Us
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WorkInProgress;
